/**
 * Audio occlusion system for Counter Pump.
 *
 * Raycasts from listener to sound source against collidable scene meshes.
 * Walls between listener and source apply:
 * - Lowpass filter: cutoff = 5000 / (1 + wallCount * 2) Hz
 * - Volume reduction: 0.7^wallCount (exponential decay)
 * - Max 3 walls checked (performance limit)
 *
 * Results are cached for 200ms per source position to avoid redundant raycasts.
 * Per-frame raycast budget: max 10 (configurable).
 */

import { Raycaster, Vector3 } from 'three';
import type { Object3D, Intersection } from 'three';

// ── Constants ────────────────────────────────────────────────────────────────

/** Maximum number of walls to consider for occlusion. */
const MAX_WALLS = 3;

/** Volume multiplier per wall (exponential decay). */
const VOLUME_PER_WALL = 0.7;

/** Base lowpass cutoff frequency (Hz) with no occlusion. */
const BASE_CUTOFF_HZ = 5000;

/** Cache TTL in milliseconds. */
const CACHE_TTL_MS = 200;

/** Default max raycasts per frame. */
const DEFAULT_MAX_RAYCASTS_PER_FRAME = 10;

/** Spatial resolution for cache key quantization (meters). */
const CACHE_QUANTIZE = 0.5;

/** Hard cap on live cache entries. LRU-evicted once exceeded. */
const CACHE_MAX_ENTRIES = 512;

// ── Public types ─────────────────────────────────────────────────────────────

export interface OcclusionResult {
  /** Number of walls detected between listener and source (0..MAX_WALLS). */
  wallCount: number;
  /** Volume multiplier (1.0 = unoccluded, 0.343 = 3 walls). */
  volumeMultiplier: number;
  /** Lowpass filter cutoff frequency in Hz (5000 = unoccluded). */
  lowpassCutoff: number;
  /** Whether any wall occludes the sound. */
  occluded: boolean;
}

// ── Pure computation functions (exported for testing) ────────────────────────

/**
 * Compute the lowpass cutoff frequency for a given wall count.
 * Formula: 5000 / (1 + wallCount * 2)
 */
export function computeLowpassCutoff(wallCount: number): number {
  const clamped = Math.max(0, Math.min(MAX_WALLS, wallCount));
  return BASE_CUTOFF_HZ / (1 + clamped * 2);
}

/**
 * Compute the volume multiplier for a given wall count.
 * Formula: 0.7^wallCount
 */
export function computeVolumeMultiplier(wallCount: number): number {
  const clamped = Math.max(0, Math.min(MAX_WALLS, wallCount));
  return Math.pow(VOLUME_PER_WALL, clamped);
}

/**
 * Compute the full occlusion result from a raw intersection count.
 */
export function computeOcclusion(intersectionCount: number): OcclusionResult {
  const wallCount = Math.max(0, Math.min(MAX_WALLS, intersectionCount));
  return {
    wallCount,
    volumeMultiplier: computeVolumeMultiplier(wallCount),
    lowpassCutoff: computeLowpassCutoff(wallCount),
    occluded: wallCount > 0,
  };
}

// ── Cache entry ──────────────────────────────────────────────────────────────

interface CacheEntry {
  result: OcclusionResult;
  timestamp: number;
}

// ── AudioOcclusionSystem ─────────────────────────────────────────────────────

export class AudioOcclusionSystem {
  private raycaster: Raycaster = new Raycaster();
  private collidableMeshes: Object3D[] = [];
  private cache: Map<string, CacheEntry> = new Map();
  private maxRaycastsPerFrame: number = DEFAULT_MAX_RAYCASTS_PER_FRAME;
  private raycastsThisFrame: number = 0;

  // Reusable vectors to avoid per-query allocations
  private _origin = new Vector3();
  private _direction = new Vector3();

  /** No-occlusion constant returned when no raycast is needed. */
  private static readonly NO_OCCLUSION: OcclusionResult = Object.freeze({
    wallCount: 0,
    volumeMultiplier: 1.0,
    lowpassCutoff: BASE_CUTOFF_HZ,
    occluded: false,
  });

  /**
   * Set the collidable meshes to raycast against.
   * Typically the scene's static collision geometry.
   */
  setCollidableMeshes(meshes: Object3D[]): void {
    this.collidableMeshes = meshes;
  }

  /**
   * Configure the maximum raycasts per frame.
   */
  setMaxRaycastsPerFrame(max: number): void {
    this.maxRaycastsPerFrame = Math.max(1, max);
  }

  /**
   * Reset the per-frame raycast budget. Call once per frame before queries.
   */
  resetFrameBudget(): void {
    this.raycastsThisFrame = 0;
  }

  /**
   * Query the occlusion between a listener position and a sound source.
   * Returns cached results if available within CACHE_TTL_MS.
   */
  query(
    listener: { x: number; y: number; z: number },
    source: { x: number; y: number; z: number },
  ): OcclusionResult {
    // No meshes -> no occlusion possible
    if (this.collidableMeshes.length === 0) {
      return AudioOcclusionSystem.NO_OCCLUSION;
    }

    // Check cache
    const key = this.cacheKey(listener, source);
    const now = Date.now();
    const cached = this.cache.get(key);
    if (cached && now - cached.timestamp < CACHE_TTL_MS) {
      // LRU touch: re-insert to move to end of insertion order.
      this.cache.delete(key);
      this.cache.set(key, cached);
      return cached.result;
    }

    // Check per-frame budget
    if (this.raycastsThisFrame >= this.maxRaycastsPerFrame) {
      // Return cached (even if stale) or no-occlusion
      return cached ? cached.result : AudioOcclusionSystem.NO_OCCLUSION;
    }

    // Perform raycast
    this.raycastsThisFrame++;
    const wallCount = this.raycastWallCount(listener, source);
    const result = computeOcclusion(wallCount);

    // Cache the result. Replace any stale entry in-place, then enforce bounds.
    if (cached) this.cache.delete(key);
    this.cache.set(key, { result, timestamp: now });
    this.evictIfNeeded(now);

    return result;
  }

  /**
   * Evict expired entries opportunistically, then LRU-evict until within cap.
   * Map iteration order is insertion order — oldest entries come first.
   */
  private evictIfNeeded(now: number): void {
    // First pass: drop expired entries (cheap when the cache is small).
    if (this.cache.size > CACHE_MAX_ENTRIES / 2) {
      for (const [k, entry] of this.cache) {
        if (now - entry.timestamp >= CACHE_TTL_MS) {
          this.cache.delete(k);
        }
      }
    }
    // Second pass: bound the total live set via LRU.
    while (this.cache.size > CACHE_MAX_ENTRIES) {
      const oldest = this.cache.keys().next().value;
      if (oldest === undefined) break;
      this.cache.delete(oldest);
    }
  }

  /**
   * Raycast from listener to source and count wall intersections.
   * Only counts intersections between listener and source (not beyond).
   */
  private raycastWallCount(
    listener: { x: number; y: number; z: number },
    source: { x: number; y: number; z: number },
  ): number {
    this._origin.set(listener.x, listener.y, listener.z);
    this._direction.set(
      source.x - listener.x,
      source.y - listener.y,
      source.z - listener.z,
    );
    const totalDistance = this._direction.length();
    if (totalDistance < 0.001) return 0;

    this._direction.divideScalar(totalDistance); // normalize

    this.raycaster.set(this._origin, this._direction);
    this.raycaster.near = 0;
    this.raycaster.far = totalDistance;

    // Collect intersections
    const intersections: Intersection[] = [];
    for (const mesh of this.collidableMeshes) {
      mesh.raycast(this.raycaster, intersections);
    }

    // Filter to only those between listener and source.
    //
    // F-AV-015: built-in `Mesh.raycast` honours `raycaster.far` so the
    // distance bound check below would be redundant for that path alone.
    // We keep it as a defensive safety net because:
    //   1. Some test mocks (e.g. audio-occlusion.test.ts:307–332) push
    //      raw intersections without honouring `raycaster.far`, so the
    //      production filter must still drop hits beyond `totalDistance`.
    //   2. Non-builtin `Object3D.raycast` overrides (custom meshes,
    //      GPU-skinned variants) are not guaranteed to clip at `.far`.
    // Do not strip — the audit-occlusion suite will fail without it.
    let wallCount = 0;
    for (const hit of intersections) {
      if (hit.distance > 0 && hit.distance < totalDistance) {
        wallCount++;
        if (wallCount >= MAX_WALLS) break;
      }
    }

    return wallCount;
  }

  /**
   * Generate a cache key from quantized positions.
   * Quantization avoids cache misses from sub-meter movement.
   */
  private cacheKey(
    listener: { x: number; y: number; z: number },
    source: { x: number; y: number; z: number },
  ): string {
    const q = CACHE_QUANTIZE;
    const lx = Math.round(listener.x / q) * q;
    const ly = Math.round(listener.y / q) * q;
    const lz = Math.round(listener.z / q) * q;
    const sx = Math.round(source.x / q) * q;
    const sy = Math.round(source.y / q) * q;
    const sz = Math.round(source.z / q) * q;
    return `${lx},${ly},${lz}->${sx},${sy},${sz}`;
  }

  /**
   * Clean up resources.
   */
  dispose(): void {
    this.cache.clear();
    this.collidableMeshes = [];
    this.raycastsThisFrame = 0;
  }
}
